import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';

const WHATSAPP_NUMBER = '918670129549';
const WHATSAPP_PREDEFINED_MESSAGE = `Hello ApkaKhata,

I'm interested in your website solutions and would like to discuss how you can help my business. Please let me know a convenient time to connect.`;

const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_PREDEFINED_MESSAGE)}`;

export default function WhatsAppFloatingButton() {
  const [isAtTop, setIsAtTop] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const currentUrl = window.location.href;

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY < 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollClick = () => {
    if (isAtTop) {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="floating-controls-container">
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="qr-modal-wrapper"
            onClick={() => setShowQR(false)}
          >
            <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="qr-close-btn" onClick={() => setShowQR(false)}>&times;</button>
              <h3>Scan to Share</h3>
              <div className="qr-code-bg">
                <QRCodeSVG value={currentUrl} size={180} level="H" includeMargin={true} fgColor="#FF6B35" />
              </div>
              <p>Scan this QR code to open this page on another device.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setShowQR(!showQR)}
        className={`scroll-btn qr-toggle-btn ${showQR ? 'active' : ''}`}
        aria-label="Show QR Code"
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="3" height="3"></rect>
          <rect x="7" y="14" width="3" height="3"></rect>
          <rect x="3" y="18" width="3" height="3"></rect>
          <rect x="7" y="18" width="3" height="3"></rect>
        </svg>
      </motion.button>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleScrollClick}
        className="scroll-btn toggle-scroll"
        aria-label={isAtTop ? "Scroll to bottom" : "Scroll to top"}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isAtTop ? 'down' : 'up'}
            initial={{ opacity: 0, rotate: -45 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 45 }}
            transition={{ duration: 0.2 }}
          >
            {isAtTop ? (
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            )}
          </motion.div>
        </AnimatePresence>
      </motion.button>

      <motion.a
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true">
          <path d="M16 0C7.164 0 0 7.164 0 16c0 2.825.738 5.49 2.032 7.784L.056 31.68l8.064-2.112A15.895 15.895 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.333c-2.614 0-5.078-.682-7.22-1.872l-.516-.304-5.344 1.4 1.428-5.22-.336-.544A13.26 13.26 0 012.667 16c0-7.364 5.97-13.333 13.333-13.333S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.316-9.964c-.392-.196-2.316-1.144-2.672-1.272-.356-.132-.614-.196-.872.196-.26.392-1.008 1.272-1.236 1.532-.228.26-.456.288-.848.092-.392-.196-1.656-.61-3.156-1.944-1.168-1.04-1.956-2.324-2.184-2.716-.228-.392-.024-.604.172-.796.176-.176.392-.456.588-.684.196-.228.26-.392.392-.652.132-.26.066-.488-.032-.684-.1-.196-.872-2.1-1.196-2.876-.314-.756-.636-.652-.872-.664l-.748-.016c-.26 0-.684.098-1.044.49-.36.392-1.372 1.34-1.372 3.268 0 1.928 1.404 3.796 1.6 4.06.196.264 2.768 4.228 6.708 5.792.936.372 1.668.596 2.24.764.9.264 1.72.226 2.368.138.728-.104 2.316-.944 2.64-1.86.324-.916.324-1.7.228-1.86-.096-.16-.356-.26-.748-.456z" />
        </svg>
      </motion.a>
    </div>
  );
}
